import { useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Copy, Check } from 'lucide-react';

export default function ListingLegend() {
  const [form, setForm] = useState({ address: '', bedrooms: 4, bathrooms: 3, sqft: 3200, features: '', style: 'luxury' });
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true); setOutput('');
    try {
      const { data } = await http.post('/ai/listing', {
        ...form,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        sqft: Number(form.sqft),
      });
      setOutput(data.output);
      toast.success('Listing crafted.');
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Listing Legend</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="listing-title">Luxury MLS Copy, Instantly.</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Describe the property. Your AI Employee delivers magazine-caliber listing copy in seconds.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={generate} className="hq-card p-8 space-y-5">
          <div><label className="hq-label">Address</label>
            <input className="hq-input" required data-testid="listing-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="1247 Beacon Hill Terrace" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="hq-label">Bedrooms</label><input className="hq-input" type="number" value={form.bedrooms} onChange={e => setForm({...form, bedrooms: e.target.value})} /></div>
            <div><label className="hq-label">Bathrooms</label><input className="hq-input" type="number" step="0.5" value={form.bathrooms} onChange={e => setForm({...form, bathrooms: e.target.value})} /></div>
            <div><label className="hq-label">Sq Ft</label><input className="hq-input" type="number" value={form.sqft} onChange={e => setForm({...form, sqft: e.target.value})} /></div>
          </div>
          <div><label className="hq-label">Style Target</label>
            <select className="hq-input" value={form.style} onChange={e => setForm({...form, style: e.target.value})}>
              <option value="luxury">Luxury</option>
              <option value="modern">Modern</option>
              <option value="family">Family</option>
              <option value="investment">Investment</option>
            </select>
          </div>
          <div><label className="hq-label">Key Features</label>
            <textarea className="hq-input" rows={5} required data-testid="listing-features" value={form.features} onChange={e => setForm({...form, features: e.target.value})} placeholder="Chef's kitchen with Viking range, primary suite with steam shower, sunlit conservatory, chef's garden, three-car carriage house…" />
          </div>
          <button disabled={loading} className="hq-btn-primary w-full" data-testid="generate-listing-btn">
            {loading ? 'Crafting…' : <><Sparkles size={12} className="inline mr-2"/> Craft Listing</>}
          </button>
        </form>

        <div className="hq-card p-8 min-h-[400px] relative">
          <div className="flex justify-between items-center mb-3">
            <span className="hq-eyebrow">Draft</span>
            {output && (
              <button onClick={copy} className="hq-btn-ghost" data-testid="copy-listing-btn">
                {copied ? <><Check size={12} className="inline mr-1"/> Copied</> : <><Copy size={12} className="inline mr-1"/> Copy</>}
              </button>
            )}
          </div>
          {loading && <div className="hq-shimmer h-full min-h-[300px]"></div>}
          {!loading && !output && (
            <div className="text-center py-24" style={{ color: 'var(--muted)' }}>
              <Sparkles size={32} className="mx-auto mb-4" style={{ color: 'var(--gold)' }} />
              <div className="font-serif text-xl" style={{ color: 'var(--sage)' }}>Your listing awaits.</div>
              <div className="text-sm mt-1">Fill the form. Your copy arrives in moments.</div>
            </div>
          )}
          {output && (
            <pre className="whitespace-pre-wrap font-serif text-base hq-fade-in" style={{ color: 'var(--ink)', lineHeight: 1.75 }} data-testid="listing-output">
              {output}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
